import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'

import Button from '../components/Button'
import Select from '../components/Select'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import SelectionBar from '../components/selection/SelectionBar'
import SortableTableHead from '../components/SortableTableHead'
import GridCircularProgress from '../components/GridCircularProgress'
import DialogDeleteIscrizione from '../components/DialogDeleteIscrizione'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { selectIscrizioniSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import { getCodiciSocieta } from '../hooks/selection-get-functions'
import {
  fromYYYYMMDDtoDDMMYYYY,
  formatNameDeroga,
} from '../hooks/utils-functions'
import {
  useGetAffiliazioniLightQuery,
  useDeleteCalendariByTeamMutation,
  useGetIscrizioniQuery,
  useLazyGetIscrizioneByIdQuery,
  useDeleteIscrizioneMutation,
} from '../hooks/api-slice'
import { setGrouping, setIscrizione } from '../container/iscrizioni'

const availableGroupings = [
  'tutte',
  'per società',
  'con deroga',
  'fuori classifica',
]

const Iscrizioni = () => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !admin,
  })
  const selectedCampionatoIscrizioniAperte = useSelector(
    state => state.selection.selectedCampionatoIscrizioniAperte,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )

  const iArgs = {
    campionato: selectedCampionato,
    codiceSocieta: admin ? undefined : codiceUtente,
  }
  const { isLoading } = useGetIscrizioniQuery(iArgs, {
    skip: !((admin || codiceUtente) && selectedCampionato),
  })
  const iscrizioni = useSelector(state => selectIscrizioniSorted(state, iArgs))
  const grouping = useSelector(state => state.iscrizioni.grouping)

  const [deleteCalendariByTeamMutation] = useDeleteCalendariByTeamMutation()
  const [triggerGetIscrizioneById] = useLazyGetIscrizioneByIdQuery()
  const [deleteIscrizioneMutation] = useDeleteIscrizioneMutation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { setFeedback } = useFeedback()

  document.title = `${config?.name} - Iscrizioni`

  let columns = []

  if (admin && grouping !== 'per società')
    columns = [
      ...columns,
      { label: 'Codice', sortable: true, sortingField: 'codiceSocieta' },
      { label: 'Società', sortable: false },
    ]

  if (
    !selectedCategoria ||
    selectedCategoria === availableCategorie[0] ||
    !admin
  )
    columns = [
      ...columns,
      { label: 'Categoria', sortable: true, sortingField: 'categoria' },
    ]

  columns = [...columns, { label: 'Nome squadra', sortable: false }]

  if (!admin)
    columns = [
      ...columns,
      { label: 'Giorno di gara', sortable: false },
      { label: 'Orario di gara', sortable: false },
    ]

  if (admin && grouping === 'con deroga')
    columns = [
      ...columns,
      { label: 'Motivo deroga', sortable: false },
      { label: 'Data deroga', sortable: false },
    ]

  if (admin && grouping === 'fuori classifica')
    columns = [
      ...columns,
      { label: 'Motivo fuori classifica', sortable: false },
      { label: 'Data fuori classifica', sortable: false },
    ]

  if (!admin || !['con deroga', 'fuori classifica'].includes(grouping))
    columns = [
      ...columns,
      { label: 'Stato', sortable: true, sortingField: 'status' },
      { label: '', sortable: false },
    ]

  // state to manage the delete dialog
  const [openDelete, setOpenDelete] = useState()
  const [deletingIscrizione, setDeletingIscrizione] = useState()

  const handleClickOpenDelete = i => {
    setDeletingIscrizione(i)
    setOpenDelete(true)
  }

  const handleCloseDelete = () => {
    setOpenDelete()
    setDeletingIscrizione()
  }

  const editIscrizione = async id => {
    const result = await triggerGetIscrizioneById(id)
    if (!result?.error) {
      dispatch(setIscrizione(result?.data))
      navigate('/iscrizione')
    }
  }

  const deleteIscrizione = async (id, deleteAlsoGare) => {
    if (admin && deleteAlsoGare) {
      const result = await deleteCalendariByTeamMutation(id)
      if (result?.error) {
        setFeedback("Errore durante l'eliminazione delle gare.")
        return
      }
    }

    const result = await deleteIscrizioneMutation(id)
    if (!result?.error) {
      setFeedback('Iscrizione eliminata correttamente.', false)
      handleCloseDelete()
    }
  }

  // set default function
  useEffect(() => {
    if (!grouping) dispatch(setGrouping(availableGroupings[0]))
  }, [dispatch, grouping])

  const IscrizioniTable = ({ codiceSocieta }) => {
    if (
      iscrizioni
        ?.filter(
          i =>
            i.categoria === selectedCategoria ||
            selectedCategoria === availableCategorie[0] ||
            !admin,
        )
        ?.filter(i => i.codiceSocieta === codiceSocieta || !codiceSocieta)
        ?.filter(i => grouping !== 'con deroga' || i.hasDeroga)
        ?.filter(i => grouping !== 'fuori classifica' || i.isFuoriClassifica)
        ?.length === 0
    )
      return null

    return (
      <Grid size={12}>
        {!!codiceSocieta && (
          <GridHeader noCenter>
            Società {codiceSocieta} -{' '}
            {
              affiliazioniLight?.find(a => a.codiceSocieta === codiceSocieta)
                ?.nomeSocieta
            }{' '}
            -{' '}
            {
              iscrizioni
                ?.filter(
                  i =>
                    i.categoria === selectedCategoria ||
                    selectedCategoria === availableCategorie[0] ||
                    !admin,
                )
                ?.filter(
                  i => i.codiceSocieta === codiceSocieta || !codiceSocieta,
                )
                ?.filter(i => grouping !== 'con deroga' || i.hasDeroga)
                ?.filter(
                  i => grouping !== 'fuori classifica' || i.isFuoriClassifica,
                )?.length
            }{' '}
            squadre iscritte
          </GridHeader>
        )}
        <TableContainer component={Paper}>
          <Table size='small'>
            <SortableTableHead
              table={iscrizioni}
              columns={columns}
              tableName='iscrizioni'
            />
            <TableBody>
              {iscrizioni
                ?.filter(
                  i =>
                    i.categoria === selectedCategoria ||
                    selectedCategoria === availableCategorie[0] ||
                    !admin,
                )
                ?.filter(
                  i => i.codiceSocieta === codiceSocieta || !codiceSocieta,
                )
                ?.filter(i => grouping !== 'con deroga' || i.hasDeroga)
                ?.filter(
                  i => grouping !== 'fuori classifica' || i.isFuoriClassifica,
                )
                ?.map((i, index) => {
                  return (
                    <TableRow
                      key={index}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: colours.white,
                        ':hover': { backgroundColor: colours.greyVeryLight },
                      }}
                      onClick={() => editIscrizione(i.id)}
                    >
                      {admin && grouping !== 'per società' && (
                        <TableCell>{i.codiceSocieta}</TableCell>
                      )}
                      {admin && grouping !== 'per società' && (
                        <TableCell>
                          {
                            affiliazioniLight?.find(
                              a => a.codiceSocieta === i.codiceSocieta,
                            )?.nomeSocieta
                          }
                        </TableCell>
                      )}
                      {(selectedCategoria === availableCategorie[0] ||
                        !admin) && (
                        <TableCell>
                          <Chip variant='outlined' label={i.categoria} />
                        </TableCell>
                      )}
                      <TableCell>{formatNameDeroga(i)}</TableCell>
                      {!admin && (
                        <TableCell>
                          {!!i.senzaPalestra
                            ? 'senza palestra'
                            : i.giorno && (
                                <Chip variant='outlined' label={i.giorno} />
                              )}
                        </TableCell>
                      )}
                      {!admin && (
                        <TableCell>
                          {!!i.senzaPalestra
                            ? 'senza palestra'
                            : i.orario && (
                                <Chip variant='outlined' label={i.orario} />
                              )}
                        </TableCell>
                      )}
                      {admin && grouping === 'con deroga' && (
                        <TableCell>{i.motivoDeroga}</TableCell>
                      )}
                      {admin && grouping === 'con deroga' && (
                        <TableCell>
                          {fromYYYYMMDDtoDDMMYYYY(i.timeDeroga)}
                        </TableCell>
                      )}
                      {admin && grouping === 'fuori classifica' && (
                        <TableCell>{i.motivoFuoriClassifica}</TableCell>
                      )}
                      {admin && grouping === 'fuori classifica' && (
                        <TableCell>
                          {fromYYYYMMDDtoDDMMYYYY(i.timeFuoriClassifica)}
                        </TableCell>
                      )}
                      {(!admin ||
                        !['con deroga', 'fuori classifica'].includes(
                          grouping,
                        )) && (
                        <TableCell>
                          <Chip
                            variant={
                              i.status === 'complete' ? 'outlined' : 'filled'
                            }
                            label={
                              i.status === 'complete'
                                ? 'completa'
                                : i.status === 'incomplete'
                                  ? 'da completare'
                                  : 'sconosciuto'
                            }
                            color={
                              i.status === 'complete'
                                ? 'success'
                                : i.status === 'incomplete'
                                  ? 'warning'
                                  : 'default'
                            }
                          />
                        </TableCell>
                      )}
                      {(!admin ||
                        !['con deroga', 'fuori classifica'].includes(
                          grouping,
                        )) && (
                        <TableCell>
                          {(admin ||
                            (i.codiceCampionato === selectedCampionato &&
                              selectedCampionatoIscrizioniAperte)) && (
                            <GridIconButton
                              type='delete'
                              onClick={e => {
                                e.stopPropagation()
                                handleClickOpenDelete(i)
                              }}
                            />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    )
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Iscrizioni' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar data={iscrizioni} yCampionato yCategoria={admin} />
        {admin && (
          <Grid size={{ xs: 12, md: 2 }}>
            <Select
              label='Raggruppamento'
              value={grouping}
              options={availableGroupings}
              optionLabels={availableGroupings.map(
                x => x?.charAt(0).toUpperCase() + x?.slice(1),
              )}
              onChange={i => dispatch(setGrouping(i))}
            />
          </Grid>
        )}
        {(admin || selectedCampionatoIscrizioniAperte) && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <Button
                to='/iscrizione'
                onClick={() => dispatch(setIscrizione())}
                children='Nuova iscrizione'
              />
            </center>
          </Grid>
        )}
      </Grid>
      {(isLoading && <GridCircularProgress />) ||
        (!iscrizioni && (
          <GridHeader text='Elenco delle iscrizioni non trovato.' />
        )) ||
        (iscrizioni.length === 0 && (
          <GridHeader text='Non è ancora stata iscritta nessuna squadra.' />
        )) || (
          <Grid container paddingY={2} sx={{ width: '100%' }}>
            {admin && (
              <Grid container spacing={3} sx={{ width: '100%' }}>
                {(selectedCampionato && iscrizioni?.length === 0 && (
                  <GridHeader text='Nessuna iscritta a questo campionato.' />
                )) ||
                  (grouping === 'per società' &&
                    getCodiciSocieta(iscrizioni)?.length > 0 &&
                    getCodiciSocieta(iscrizioni).map(codiceSocieta => (
                      <IscrizioniTable
                        key={codiceSocieta}
                        codiceSocieta={codiceSocieta}
                      />
                    ))) || <IscrizioniTable />}
              </Grid>
            )}
            {!admin && <IscrizioniTable />}
          </Grid>
        )}
      <DialogDeleteIscrizione
        open={openDelete}
        handleClose={handleCloseDelete}
        triggerFunction={deleteIscrizione}
        team={deletingIscrizione}
      />
    </Grid>
  )
}

export default Iscrizioni
